
import { useEffect, useState } from "react";
import { Canvas, PencilBrush, Shadow } from "fabric";
import { toast } from "@/hooks/use-toast";
import { CustomCanvas } from "@/types/fabric";
import { CustomPencilBrush } from "@/lib/custom-pencil-brush";

export type Tool = "draw" | "incision" | "transform" | "ruler" | "angle" | "shapes" | "scalpel" | "add" | null;

interface UseCanvasSetupProps {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  activeTool: Tool;
  brushSize: number;
  setCurrentDrawingId: (id: string | null) => void;
  setProjectTitle: (title: string) => void;
  setActiveTool: (tool: Tool) => void;
}

export const useCanvasSetup = ({
  canvasRef,
  activeTool,
  brushSize,
  setCurrentDrawingId,
  setProjectTitle,
  setActiveTool,
}: UseCanvasSetupProps) => {
  const [canvas, setCanvas] = useState<CustomCanvas | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Check if there's already a fabric canvas instance for this element
    const canvasId = canvasRef.current.id;
    const existingCanvas = (Canvas as any)._cache?.findByElement && (Canvas as any)._cache.findByElement(canvasRef.current);
    
    if (existingCanvas) {
      console.log("Reusing existing Fabric.js canvas instance");
      setCanvas(existingCanvas as CustomCanvas);
      return;
    }

    console.log("Creating new Fabric.js canvas instance");
    const fabricCanvas = new Canvas(canvasId, {
      isDrawingMode: true,
      width: window.innerWidth,
      height: window.innerHeight,
      backgroundColor: 'transparent',
      preserveObjectStacking: true
    });

    // Initialize custom brush with raw mode by default
    fabricCanvas.freeDrawingBrush = new CustomPencilBrush(fabricCanvas);
    fabricCanvas.freeDrawingBrush.width = brushSize;
    
    if (fabricCanvas.freeDrawingBrush instanceof CustomPencilBrush) {
      fabricCanvas.freeDrawingBrush.decimate = 2;
      fabricCanvas.freeDrawingBrush.setRawMode(true); // Set raw mode as default
    }

    // Add selection event listeners
    const handleSelection = () => {
      fabricCanvas.requestRenderAll();
    };

    fabricCanvas.on('selection:created', handleSelection);
    fabricCanvas.on('selection:updated', handleSelection);
    fabricCanvas.on('selection:cleared', handleSelection);

    // Initialize canvas data
    (fabricCanvas as CustomCanvas).data = {
      currentDrawingId: null,
      lastModified: new Date().toISOString(),
      lastLoaded: null,
      showMeasurements: true // Initialize measurement visibility to true
    };

    // Handle window resize
    const handleResize = () => {
      fabricCanvas.setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
      fabricCanvas.renderAll();
    };

    window.addEventListener('resize', handleResize);
    setCanvas(fabricCanvas);

    return () => {
      fabricCanvas.off('selection:created', handleSelection);
      fabricCanvas.off('selection:updated', handleSelection);
      fabricCanvas.off('selection:cleared', handleSelection);
      window.removeEventListener('resize', handleResize);
      fabricCanvas.dispose();
    };
  }, []);

  // Handle tool changes
  useEffect(() => {
    if (!canvas) return;
    
    if (!activeTool) {
      canvas.isDrawingMode = false;
      canvas.selection = true; // Allow selection even without active tool
      canvas.getObjects().forEach(obj => {
        obj.selectable = true; // Keep objects selectable
        obj.evented = true;   // Keep objects interactive
      });
    } else if (activeTool === "transform" || activeTool === "shapes" || activeTool === "add") {
      canvas.isDrawingMode = false;
      canvas.selection = true;
      canvas.getObjects().forEach(obj => {
        obj.selectable = true;
        obj.evented = true;
      });
    } else if (activeTool === "draw" || activeTool === "incision" || activeTool === "scalpel") {
      canvas.isDrawingMode = true;
      const style = penStyles[activeTool] || penStyles.draw; // Default to draw style if not found
      
      if (canvas.freeDrawingBrush instanceof CustomPencilBrush) {
        canvas.freeDrawingBrush.color = style.color;
        canvas.freeDrawingBrush.width = style.width + 2;
        canvas.freeDrawingBrush.strokeLineCap = 'round';
        canvas.freeDrawingBrush.strokeLineJoin = 'round';
        canvas.freeDrawingBrush.shadow = new Shadow({
          color: '#FFFFFF',
          blur: style.width + 1,
          offsetX: 0,
          offsetY: 0
        });
      }
    } else {
      canvas.isDrawingMode = false;
    }
    
    canvas.renderAll();
  }, [activeTool, brushSize, canvas]);

  const clearCanvas = () => {
    if (!canvas) {
      console.error('Canvas is null, cannot clear');
      return;
    }
    
    console.log('Clearing canvas and resetting state - function called');
    
    try {
      // First, remove all objects
      const objects = canvas.getObjects();
      console.log(`Removing ${objects.length} objects from canvas`);
      
      // Remove objects one by one to ensure they're all cleared
      while (canvas.getObjects().length) {
        const obj = canvas.getObjects()[0];
        canvas.remove(obj);
      }
      
      // Reset canvas properties
      canvas.backgroundColor = 'transparent';
      
      // Reset viewport and zoom
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      
      // Clear the canvas data completely and ensure it's a new object
      (canvas as CustomCanvas).data = {
        currentDrawingId: null,
        lastModified: new Date().toISOString(),
        lastLoaded: null
      };
      
      // Force a full re-render
      canvas.renderAll();
      
      // Reset all state
      setCurrentDrawingId(null);
      setProjectTitle("Untitled Project");
      setActiveTool(null);
      
      console.log('Canvas cleared successfully, new state:', {
        canvasData: (canvas as CustomCanvas).data,
        objects: canvas.getObjects().length
      });
      
      toast({
        title: "Canvas cleared",
        description: "All drawings have been removed from the canvas.",
      });
    } catch (error) {
      console.error('Error while clearing canvas:', error);
      toast({
        title: "Error clearing canvas",
        description: "An error occurred while clearing the canvas.",
        variant: "destructive"
      });
    }
  };

  const penStyles = {
    draw: {
      color: '#9b87f5',
      width: 1.5
    },
    incision: {
      color: '#1E40AF',
      width: 1.5
    },
    scalpel: {
      color: '#1E40AF',
      width: 2.0
    }
  };

  return {
    canvas,
    clearCanvas,
  };
};
